import Tags from "components/black/Tags";
import PostReactions from "components/reactions/PostReactions";
import RelativeTime from "components/ui/RelativeTime";
import classNames from "components/ui/classNames";
import { LinkButton } from "lib/miolo";
import { LockIcon, PinIcon } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import type { IAssetDiscussionCommentFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentFields.generated";
import type { IAssetDiscussionCommentParticipantsFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentParticipantsFields.generated";
import type { IAssetDiscussionCommentRepliesPaginationFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentRepliesPaginationFields.generated";
import useSelfie from "../../lib/hooks/useSelfie";
import translateTypenameIntoAssetType from "../../lib/utils/translateTypenameIntoAssetType";
import { IReactionSubject } from "../../types/graphqlTypes";
import Avatar from "../core/Avatar";
import { Menu, MenuActionButtonItem, MenuDivider, MenuTitle } from "../core/menu";
import TextTooltip from "../shared/TextTooltip";
import ActionsDropdown from "./ActionsDropdown";
import AssetDiscussionCommentParticipants from "./AssetDiscussionCommentParticipants";
import AssetDiscussionCommentReply from "./AssetDiscussionCommentReply";
import CommentContent from "./CommentContent";
import HorizontalLineDivider from "./HorizontalLineDivider";
import InlineAssetDiscussionCommentError from "./InlineAssetDiscussionCommentError";
import ViewMoreReplies from "./ViewMoreReplies";
import IndividualCommentInputForm from "./form/IndividualCommentInputForm";
import ReplyCommentInputForm from "./form/ReplyCommentInputForm";
import translateServerErrorIntoFriendlyMessage from "./form/translateServerErrorIntoFriendlyMessage";
import useCommentActions from "./hooks/useCommentActions";
import ReplyToAssetDiscussionCommentMutation from "./mutations/ReplyToAssetDiscussionCommentMutation";

interface IAssetDiscussionCommentProps {
  comment: IAssetDiscussionCommentFieldsFragment;
  participants?: IAssetDiscussionCommentParticipantsFieldsFragment;
  replies?: IAssetDiscussionCommentRepliesPaginationFieldsFragment;
  allowToReplyRootComment?: boolean;
  allowToReplyIndividualReplies?: boolean;
  shouldDisplayReplies?: boolean;
}

export default function AssetDiscussionComment({
  comment,
  participants,
  replies,
  allowToReplyRootComment = true,
  allowToReplyIndividualReplies = true,
  shouldDisplayReplies = false,
}: IAssetDiscussionCommentProps) {
  const selfie = useSelfie();

  const [isEditingContent, setIsEditingContent] = useState<boolean>(false);
  const [isWritingReply, setIsWritingReply] = useState<boolean>(false);
  const [isShowingReplies, setIsShowingReplies] = useState<boolean>(shouldDisplayReplies);

  const {
    destroyComment,
    editComment,
    lockComment,
    unlockComment,
    pinComment,
    unpinComment,

    isDestroyingComment,
    isLockingComment,
    isUnlockingComment,
    isPinningComment,
    isUnpinningComment,

    destroyCommentError,
    editCommentError,
    lockCommentError,
    unlockCommentError,
    pinCommentError,
    unpinCommentError,
  } = useCommentActions(comment);

  const viewerCanTakeActions = comment.viewerCanEdit || comment.viewerCanDestroy || (selfie && selfie.isSiteAdmin);

  return (
    <div className="box-border flex font-aeonik font-normal">
      <div className="relative mr-4">
        <Avatar
          src={comment.author.pictureUrl}
          hashValue={comment.author._id}
          alt={comment.author.fullName}
          squircle={true}
          variant="rounded"
          size={42}
        />

        {isShowingReplies && replies && replies.nodes.length > 0 && (
          <div
            className="absolute top-[42] mt-4 w-px bg-slate-100 dark:bg-slate-950"
            style={{
              left: "calc(50% - 2px)",
              height: "calc(100% - 42px - 16px)",
              content: '""',
            }}
          />
        )}
      </div>

      <div className="w-full space-y-4">
        {comment.isLocked && (
          <div
            className={classNames(
              "mb-2 mt-0 flex items-center gap-x-1 text-neutral-700",
              "dark:font-medium dark:text-red-300",
            )}
          >
            <LockIcon className="size-4" absoluteStrokeWidth={true} />
            <span className="text-sm tracking-wider">
              <FormattedMessage defaultMessage="Comentário trancado" id="Kf1Xnh" />
            </span>
          </div>
        )}

        {comment.isPinned && comment.pinnedBy && (
          <div
            className={classNames(
              "mb-2 mt-0 flex items-center gap-x-1 text-neutral-700",
              "dark:font-medium dark:text-stone-300",
            )}
          >
            <PinIcon className="size-4" absoluteStrokeWidth={true} />
            <span className="text-sm tracking-wider">
              <FormattedMessage defaultMessage="Comentário fixado" id="i6qyUs" />
            </span>
          </div>
        )}

        <div className="mt-0 flex flex-col">
          <div className="flex items-center space-x-2">
            <strong className="text-sm dark:text-slate-200">{comment.author.fullName}</strong>
            <Tags viewer={comment.author} />
          </div>

          <div>
            <Link
              href="/comments/[shortId]"
              as={`/comments/${comment.shortId}`}
              passHref={true}
              className="inline-flex hover:underline"
            >
              <RelativeTime
                className="text-xs font-medium text-slate-500 dark:text-gray-500 md:text-sm"
                timeStamp={comment.postedAt}
              />
            </Link>

            {comment.lastEditedAt && (
              <span className="text-xs font-medium text-slate-500 md:text-sm">{" • editado"}</span>
            )}
          </div>
        </div>

        {isEditingContent ? (
          <>
            <IndividualCommentInputForm
              submitButtonText="Editar"
              onSubmit={async (bodyAsPlainText) => {
                await editComment(bodyAsPlainText);
                setIsEditingContent(false);
              }}
              onCancel={() => setIsEditingContent(false)}
              bodyAsPlainText={comment.bodyAsPlainText}
            />

            <HorizontalLineDivider />
          </>
        ) : (
          <CommentContent bodyAsPlainText={comment.bodyAsPlainText} />
        )}

        <PostReactions
          subjectId={comment._id}
          subjectType={IReactionSubject.AssetDiscussionComment}
          reactable={comment}
        />

        <div className="grid grid-cols-2">
          <div className="grid auto-cols-max grid-flow-col gap-x-3">
            {comment.viewerCanReply && allowToReplyRootComment && replies && replies.totalCount > 0 && (
              <LinkButton
                variant="primary"
                onClick={() => {
                  if (isShowingReplies && isWritingReply) {
                    setIsWritingReply((previous) => !previous);
                  }

                  setIsShowingReplies((previous) => !previous);
                }}
              >
                {!isShowingReplies ? `Ver respostas (${replies.totalCount})` : "Ocultar respostas"}
              </LinkButton>
            )}

            {comment.viewerCanReply &&
              allowToReplyRootComment &&
              ((replies && replies.totalCount === 0) || isShowingReplies) && (
                <LinkButton
                  variant={isWritingReply ? "danger" : "secondary"}
                  onClick={() => {
                    setIsWritingReply(!isWritingReply);
                  }}
                >
                  {isWritingReply ? (
                    <FormattedMessage defaultMessage="Cancelar" id="nZLeaQ" />
                  ) : (
                    <FormattedMessage defaultMessage="Responder" id="e/WuSx" />
                  )}
                </LinkButton>
              )}

            {!comment.viewerCanReply && replies && replies.totalCount > 0 && (
              <TextTooltip
                id={`tooltips/mustBePremiumToViewReplies/${comment.shortId}`}
                placement="top"
                content="Você precisa ser Premium para ver as respostas"
              >
                {({ accessibility, bind, ref }) => {
                  return (
                    <span
                      {...accessibility}
                      ref={ref}
                      {...bind}
                      className="cursor-default select-none text-sm font-medium text-blue-600"
                    >
                      Respostas ({replies.totalCount})
                    </span>
                  );
                }}
              </TextTooltip>
            )}

            {viewerCanTakeActions && (
              <ActionsDropdown>
                {({ closeOnClick }) => (
                  <Menu>
                    {comment.viewerCanEdit && (
                      <MenuActionButtonItem
                        variant="primary"
                        onClick={closeOnClick(() => setIsEditingContent(!isEditingContent))}
                      >
                        Editar
                      </MenuActionButtonItem>
                    )}

                    {selfie && selfie.isSiteAdmin && (
                      <>
                        <MenuActionButtonItem
                          variant="primary"
                          onClick={closeOnClick(() => {
                            navigator.clipboard.writeText(comment.author.email);
                          })}
                        >
                          Copiar email
                        </MenuActionButtonItem>

                        <MenuActionButtonItem
                          variant="primary"
                          onClick={closeOnClick(() => {
                            navigator.clipboard.writeText(comment.author._id);
                          })}
                        >
                          Copiar ID
                        </MenuActionButtonItem>

                        <MenuDivider />

                        <MenuTitle>Moderação</MenuTitle>

                        {comment.isPinned ? (
                          <MenuActionButtonItem disabled={isUnpinningComment} onClick={closeOnClick(unpinComment)}>
                            {isUnpinningComment ? "Desfixando..." : "Desfixar"}
                          </MenuActionButtonItem>
                        ) : (
                          <MenuActionButtonItem disabled={isPinningComment} onClick={closeOnClick(pinComment)}>
                            {isPinningComment ? "Fixando..." : "Fixar"}
                          </MenuActionButtonItem>
                        )}

                        {comment.isLocked ? (
                          <MenuActionButtonItem disabled={isUnlockingComment} onClick={closeOnClick(unlockComment)}>
                            {isUnlockingComment ? "Destrancando..." : "Destrancar"}
                          </MenuActionButtonItem>
                        ) : (
                          <MenuActionButtonItem disabled={isLockingComment} onClick={closeOnClick(lockComment)}>
                            {isLockingComment ? "Trancando..." : "Trancar"}
                          </MenuActionButtonItem>
                        )}
                      </>
                    )}

                    <MenuDivider />

                    {comment.viewerCanDestroy && (
                      <MenuActionButtonItem
                        variant="danger"
                        disabled={isDestroyingComment}
                        onClick={() => destroyComment()}
                      >
                        {isDestroyingComment ? "Apagando..." : "Apagar"}
                      </MenuActionButtonItem>
                    )}
                  </Menu>
                )}
              </ActionsDropdown>
            )}
            {participants && participants.nodes.length > 0 && (
              <div>
                <AssetDiscussionCommentParticipants
                  participants={participants.nodes}
                  totalCount={participants.totalCount}
                />
              </div>
            )}
          </div>
        </div>

        {selfie && isWritingReply && (
          <>
            <HorizontalLineDivider />

            <ReplyToAssetDiscussionCommentMutation>
              {(replyToAssetDiscussionComment) => (
                <div>
                  <ReplyCommentInputForm
                    viewer={selfie}
                    autoFocus={true}
                    onCancel={() => setIsWritingReply(false)}
                    onSubmit={async (bodyAsPlainText) => {
                      await replyToAssetDiscussionComment({
                        assetId: comment.asset._id,
                        assetType: translateTypenameIntoAssetType(comment.asset.__typename),
                        replyToCommentId: comment._id,
                        replyToCommentShortId: comment.shortId,
                        bodyAsPlainText,
                      });

                      setIsWritingReply(false);
                    }}
                  />
                </div>
              )}
            </ReplyToAssetDiscussionCommentMutation>

            {replies && replies.totalCount > 0 && <HorizontalLineDivider />}
          </>
        )}

        {firstError([
          destroyCommentError,
          editCommentError,
          lockCommentError,
          unlockCommentError,
          pinCommentError,
          unpinCommentError,
        ]) && (
          <div>
            <InlineAssetDiscussionCommentError>
              {translateServerErrorIntoFriendlyMessage(
                firstError([
                  destroyCommentError,
                  editCommentError,
                  lockCommentError,
                  unlockCommentError,
                  pinCommentError,
                  unpinCommentError,
                ]) || new Error("Ih! Tem algo errado...tente novamente mais tarde."),
              )}
            </InlineAssetDiscussionCommentError>
          </div>
        )}

        {isShowingReplies && replies && (
          <>
            {!isWritingReply && <HorizontalLineDivider />}

            {replies.nodes.length > 0 && (
              <div className="divide-y divide-slate-100 dark:divide-slate-950">
                {replies.nodes.map((reply) => {
                  return (
                    <div key={reply.shortId} className="py-4 first:pt-0 last:pb-0">
                      <AssetDiscussionCommentReply
                        reply={reply}
                        rootComment={comment}
                        allowToReply={allowToReplyIndividualReplies}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {replies.pageInfo.hasNextPage && (
              <ViewMoreReplies assetId={comment.asset._id} rootCommentShortId={comment.shortId} repliesPerPage={8} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function firstError(arrayOfErrors: (Error | null)[]): Error | null {
  return arrayOfErrors.find((error) => error !== null) || null;
}
